import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { RichEditor } from '../components/RichEditor'
import { Loader2, ArrowLeft, Save } from 'lucide-react'
import { apiGet } from '../lib/api'

interface PostForm {
  title: string
  slug: string
  excerpt: string
  content: string
  category_id: string
  image: string
  is_published: boolean
  published_at: string
}

const empty: PostForm = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  category_id: '',
  image: '',
  is_published: false,
  published_at: '',
}

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 180)
}

interface Props {
  mode: 'create' | 'edit'
}

export function BlogPostForm({ mode }: Props) {
  const params = mode === 'edit' ? useParams({ strict: false }) : { id: '' }
  const id = (params as any).id as string
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState<PostForm>(empty)
  const [err, setErr] = useState<string | null>(null)
  const [autoSlug, setAutoSlug] = useState(mode === 'create')

  const { data, isLoading } = useQuery<any>({
    queryKey: ['blogPost', id],
    queryFn: () => apiGet(`/api/admin/blog/${id}`),
    enabled: mode === 'edit',
  })

  useEffect(() => {
    if (mode === 'edit' && data) {
      setForm({
        title: data.titulo || '',
        slug: data.slug || '',
        excerpt: data.excerpt || '',
        content: data.contenido || '',
        category_id: data.categoriaId || '',
        image: data.imagen || '',
        is_published: !!data.publicado,
        published_at: data.fechaPublicacion || '',
      })
      setAutoSlug(false)
    }
  }, [data, mode])

  const save = useMutation({
    mutationFn: async (payload: PostForm) => {
      const url = mode === 'edit' ? `/api/admin/blog/${id}` : `/api/admin/blog`
      const method = mode === 'edit' ? 'PUT' : 'POST'
      const body = {
        ...payload,
        category_id: payload.category_id ? payload.category_id : null,
        published_at: payload.published_at || null,
      }
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blog'] })
      navigate({ to: '/blog' })
    },
    onError: (e: Error) => setErr(e.message),
  })

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    save.mutate(form)
  }

  function set<K extends keyof PostForm>(k: K, v: PostForm[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  function onTitleChange(v: string) {
    set('title', v)
    if (autoSlug) set('slug', slugify(v))
  }

  const title = mode === 'create' ? 'Nuevo post' : 'Editar post'

  return (
    <>
      <Topbar
        title={title}
        subtitle={form.slug || undefined}
        actions={
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <ArrowLeft className="size-3.5" /> Cancelar
          </Link>
        }
      />

      <div className="p-6">
        {mode === 'edit' && isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
          </div>
        )}

        {(mode === 'create' || !isLoading) && (
          <form onSubmit={onSubmit} className="mx-auto max-w-4xl space-y-6">
            {err && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {err}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <div className="grid grid-cols-1 gap-4">
                    <Field label="Título *">
                      <input
                        required
                        value={form.title}
                        onChange={(e) => onTitleChange(e.target.value)}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-base font-semibold outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="Slug *">
                      <div className="flex gap-2">
                        <input
                          required
                          value={form.slug}
                          onChange={(e) => {
                            setAutoSlug(false)
                            set('slug', e.target.value)
                          }}
                          className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setAutoSlug(true)
                            set('slug', slugify(form.title))
                          }}
                          className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs font-medium text-ink-700 hover:bg-ink-50"
                        >
                          Auto
                        </button>
                      </div>
                    </Field>
                    <Field label="Resumen (excerpt)">
                      <textarea
                        rows={3}
                        value={form.excerpt}
                        onChange={(e) => set('excerpt', e.target.value)}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="Contenido">
                      <RichEditor
                        value={form.content}
                        onChange={(v) => set('content', v)}
                        placeholder="Escribe el contenido del post…"
                        minHeight={400}
                      />
                    </Field>
                  </div>
                </div>
              </div>

              <div className="space-y-6 lg:col-span-1">
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h2 className="mb-4 font-semibold text-ink-900">Publicación</h2>
                  <div className="space-y-4">
                    <label className="inline-flex items-center gap-2 text-sm text-ink-700">
                      <input
                        type="checkbox"
                        checked={form.is_published}
                        onChange={(e) => set('is_published', e.target.checked)}
                        className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                      />
                      Publicado
                    </label>
                    <Field label="Fecha de publicación">
                      <input
                        type="date"
                        value={form.published_at}
                        onChange={(e) => set('published_at', e.target.value)}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                  </div>
                </div>

                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h2 className="mb-4 font-semibold text-ink-900">Metadatos</h2>
                  <div className="space-y-4">
                    <Field label="ID Categoría">
                      <input
                        value={form.category_id}
                        onChange={(e) => set('category_id', e.target.value)}
                        placeholder="Ej. 1, 2, 3..."
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="URL imagen destacada">
                      <input
                        value={form.image}
                        onChange={(e) => set('image', e.target.value)}
                        placeholder="https://..."
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                      />
                      {form.image && (
                        <img
                          src={form.image}
                          alt=""
                          className="mt-2 h-32 w-full rounded-lg object-cover"
                          onError={(e) => {
                            ;(e.target as HTMLImageElement).style.display = 'none'
                          }}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Link
                to="/blog"
                className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={save.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {save.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Save className="size-3.5" />
                )}
                {mode === 'create' ? 'Crear post' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      {children}
    </div>
  )
}
